import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { CustomsStatusDocument } from "../../graphql/graphql";
import { formatDateTime } from "../../shared/Utils";

// Type that flattens CustomsStatusDocument into a table row
export type FilingPackStatusRow = {
  filingPackId: string;
  status?: string | null;
  timestamp?: string | null;
  brokerReferenceNumber?: string | null;
  customsReferenceNumber?: string | null;
  message?: string | null;
};

export type FilingPackStatusTableColumn<T> = ColumnDef<T, any> & {
  accessorKey: keyof T | string;
  header: string | React.ReactNode | (() => React.ReactNode);
};

// Helper function to flatten FilingPack data into rows
export const flattenFilingPacksToStatusRows = (docs: (CustomsStatusDocument)[]): FilingPackStatusRow[] => {
  const rows: FilingPackStatusRow[] = [];

  // if the doc has a status property then add it to the rows
  docs.forEach(doc => {
    if ('status' in doc) {
      rows.push({
        filingPackId: doc.filingPackId || "N/A",
        status: doc.status || null,
        timestamp: doc.timestamp || null,
        brokerReferenceNumber: doc.brokerReferenceNumber || null,
        customsReferenceNumber: doc.customsReferenceNumber || null,
        message: doc.message || null,
      });
    }
  });
  
  return rows.sort((a, b) => {
    // Sort by timestamp ascending, treating null/undefined as oldest
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timeA - timeB;
  });
};

export const getFilingPackStatusTableColumns = (): FilingPackStatusTableColumn<FilingPackStatusRow>[] => [
  {
    header: () => <span title="Filing Pack ID">Filing Pack ID</span>,
    accessorKey: "filingPackId",
    size: 160,
    cell: ({ row }) => {
      const value = row.original.filingPackId || "N/A";
      return (
        <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
          {value}
        </span>
      );
    }
  },
  {
    header: () => <span title="Status">Status</span>,
    accessorKey: "status",
    size: 120,
    cell: ({ row }) => {
      const value = row.original.status || "N/A";
      return (
        <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
          {value}
        </span>
      );
    }
  },
  {
    header: () => <span title="Broker Reference">Broker Ref</span>,
    accessorKey: "brokerReferenceNumber",
    size: 140,
    cell: ({ row }) => {
      const value = row.original.brokerReferenceNumber || "N/A";
      return (
        <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
          {value}
        </span>
      );
    }
  },
  {
    header: () => <span title="Customs Reference">Customs Ref</span>,
    accessorKey: "customsReferenceNumber",
    size: 140,
    cell: ({ row }) => {
      const value = row.original.customsReferenceNumber || "N/A";
      return (
        <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
          {value}
        </span>
      );
    }
  },
  {
    header: () => <span title="Timestamp">Timestamp</span>,
    accessorKey: "timestamp",
    size: 160,
    cell: ({ row }) => {
      const value = row.original.timestamp 
        ? formatDateTime(row.original.timestamp)
        : "N/A";
      return (
        <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
          {value}
        </span>
      );
    }
  },
];